import { useState, useEffect } from 'react';
import { Smartphone, Trash2, Loader as Loader2, Plus, Monitor } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  getTrustedDevices,
  removeTrustedDevice,
  removeAllTrustedDevices,
  trustCurrentDevice,
  generateDeviceFingerprint,
  type TrustedDevice,
} from '@/lib/trustedDevices';

export default function TrustedDevicesSection() {
  const { user } = useAuth();
  const [devices, setDevices] = useState<TrustedDevice[]>([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState<string | null>(null);
  const [trusting, setTrusting] = useState(false);
  const [removingAll, setRemovingAll] = useState(false);

  const currentFingerprint = generateDeviceFingerprint();

  useEffect(() => {
    if (!user) return;
    loadDevices();
  }, [user]);

  async function loadDevices() {
    if (!user) return;
    setLoading(true);
    try {
      const data = await getTrustedDevices(user.id);
      setDevices(data);
    } catch {
      setDevices([]);
    } finally {
      setLoading(false);
    }
  }

  async function handleRemove(deviceId: string) {
    setRemoving(deviceId);
    try {
      await removeTrustedDevice(deviceId);
      setDevices((prev) => prev.filter((d) => d.id !== deviceId));
    } catch {
    } finally {
      setRemoving(null);
    }
  }

  async function handleRemoveAll() {
    if (!user) return;
    setRemovingAll(true);
    try {
      await removeAllTrustedDevices(user.id);
      setDevices([]);
    } catch {
    } finally {
      setRemovingAll(false);
    }
  }

  async function handleTrustCurrent() {
    if (!user) return;
    setTrusting(true);
    try {
      await trustCurrentDevice(user.id);
      await loadDevices();
    } catch {
    } finally {
      setTrusting(false);
    }
  }

  const isCurrentDeviceTrusted = devices.some(
    (d) => d.device_fingerprint === currentFingerprint
  );

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center gap-3 py-2.5 px-3">
        <Loader2 size={15} className="animate-spin text-white" />
        <span className="text-xs text-white">Loading devices...</span>
      </div>
    );
  }

  return (
    <div className="py-1">
      <div className="flex items-center gap-3 py-2.5 px-3">
        <div className="w-9 h-9 rounded-xl bg-teal-500/10 flex items-center justify-center flex-shrink-0">
          <Monitor size={15} className="text-teal-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white">Trusted Devices</p>
          <p className="text-[11px] text-white">
            {devices.length === 0
              ? 'No trusted devices'
              : `${devices.length} device${devices.length !== 1 ? 's' : ''} trusted`}
          </p>
        </div>
        {!isCurrentDeviceTrusted && (
          <button
            onClick={handleTrustCurrent}
            disabled={trusting}
            className="flex items-center gap-1 px-2.5 py-1.5 text-[11px] font-semibold bg-teal-500/10 border border-teal-200 rounded-xl text-teal-400 glass-hover-teal disabled:opacity-50"
          >
            {trusting ? (
              <Loader2 size={11} className="animate-spin" />
            ) : (
              <Plus size={11} />
            )}
            Trust This
          </button>
        )}
      </div>

      {devices.length > 0 && (
        <>
          <div className="mx-3 mt-1 mb-2 space-y-1.5">
            {devices.map((device) => {
              const isCurrent = device.device_fingerprint === currentFingerprint;
              return (
                <div
                  key={device.id}
                  className={`flex items-center gap-2.5 px-3 py-2 rounded-xl ${
                    isCurrent ? 'bg-teal-500/10 border border-teal-500/15' : 'glass-inner'
                  }`}
                >
                  <Smartphone size={13} className={isCurrent ? 'text-teal-400' : 'text-white'} />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-white truncate">
                      {device.device_name}
                      {isCurrent && (
                        <span className="ml-1.5 text-[10px] font-semibold text-teal-400 bg-teal-500/15 px-1.5 py-0.5 rounded-md">
                          Current
                        </span>
                      )}
                    </p>
                    <p className="text-[10px] text-white">
                      Expires {formatDate(device.expires_at)}
                    </p>
                  </div>
                  <button
                    onClick={() => handleRemove(device.id)}
                    disabled={removing === device.id}
                    className="p-1.5 glass-hover-red rounded-lg text-white hover:text-red-500"
                  >
                    {removing === device.id ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Trash2 size={12} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {devices.length > 1 && (
            <div className="mx-3 mb-2">
              <button
                onClick={handleRemoveAll}
                disabled={removingAll}
                className="w-full py-2 text-[11px] font-semibold text-red-500 glass-hover-red rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                {removingAll ? (
                  <Loader2 size={11} className="animate-spin" />
                ) : (
                  <Trash2 size={11} />
                )}
                Remove All Trusted Devices
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
